// Fond de carte vectoriel PMTiles.
//
// Toute la région tient dans UN fichier statique lu en requêtes HTTP Range, ou hors ligne
// depuis un Blob en IndexedDB. Ce choix remplace les tuiles raster CARTO, dont les
// conditions d'utilisation interdisent le téléchargement pour usage hors ligne.
// Données © contributeurs OpenStreetMap (ODbL).

import './leaflet-global.js';   // doit précéder protomaps-leaflet (voir ce module)
import { leafletLayer } from 'protomaps-leaflet';
import { PMTiles, FileSource } from 'pmtiles';
import { getFile, putFile, deleteFile } from './storage.js';

const BASEMAP_PATH = '/basemap.pmtiles';
const BASEMAP_KEY = 'basemap';
const MAX_DATA_ZOOM = 15;   // zoom maximal présent dans l'extrait régional

// Renvoie la couche Leaflet, en privilégiant la copie hors ligne si elle existe.
async function createBasemapLayer() {
    let url = BASEMAP_PATH;
    let offline = false;
    try {
        const blob = await getFile(BASEMAP_KEY);
        if (blob && blob.size > 0) {
            // FileSource lit .name, .slice() et .arrayBuffer(). Un Blob nu n'a pas de .name
            // et getKey() renverrait undefined, ce qui casse le cache interne de pmtiles.
            const file = new File([blob], 'basemap.pmtiles');
            url = new PMTiles(new FileSource(file));
            offline = true;
        }
    } catch (e) { /* stockage indisponible : on reste sur le réseau */ }

    const layer = leafletLayer({
        url,
        flavor: 'light',
        lang: 'fr',
        maxDataZoom: MAX_DATA_ZOOM,
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
    });
    return { layer, offline };
}

// Télécharge le fichier régional et le range en IndexedDB. onProgress reçoit (reçu, total).
async function downloadBasemap(onProgress) {
    const res = await fetch(BASEMAP_PATH, { cache: 'no-store' });
    if (!res.ok) throw new Error('HTTP ' + res.status);
    const total = Number(res.headers.get('content-length')) || 0;

    // Lecture en flux pour pouvoir afficher une progression : 17 Mo sur un lien mobile,
    // une barre inerte donnerait l'impression que l'app est figée.
    const reader = res.body.getReader();
    const chunks = [];
    let received = 0;
    for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        received += value.length;
        if (onProgress) onProgress(received, total);
    }
    const blob = new Blob(chunks, { type: 'application/octet-stream' });
    await putFile(BASEMAP_KEY, blob);
    return blob.size;
}

async function basemapStatus() {
    try {
        const blob = await getFile(BASEMAP_KEY);
        return { offline: !!(blob && blob.size), bytes: blob ? blob.size : 0 };
    } catch (e) { return { offline: false, bytes: 0 }; }
}

function removeBasemap() { return deleteFile(BASEMAP_KEY); }

export { createBasemapLayer, downloadBasemap, basemapStatus, removeBasemap, BASEMAP_PATH };
